import { api } from "./api";
import { isValidE164PhoneNumber } from "@/helpers/phone-number";

export enum VerificationType {
  Email = 1,
  PhoneNumber,
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const startEmailVerification = async(emailAddress: string): Promise<any> => {
  const {data} = await api.post<any>(`/Verification/email?emailAddress=${encodeURIComponent(emailAddress)}`);
  return { success: true, errors: null, instanceId: data };
}

export const startPhoneNumberVerification = async(phoneNumber: string): Promise<any> => {
  if (!isValidE164PhoneNumber(phoneNumber)) {
    throw new Error("Enter a valid phone number before requesting a verification code.");
  }

  const {data} = await api.post<any>(`/Verification/phone?phoneNumber=${encodeURIComponent(phoneNumber)}`);
  return { success: true, errors: null, instanceId: data };
}

const getInstanceVerification = async (instanceId: string): Promise<any> => {
  const queryStatusUrl = `/Verification/${instanceId}`;
  const response = await api.get<any>(queryStatusUrl);
  const customStatus = response.data?.customStatus;

  if (response.status != 202 && response.status != 200) {
    return {Verified: true, CodeCorrect: false, ErrorMessage: "Code can't be verified. Request new code."};
  } else if (customStatus?.ErrorMessage) {
    return {Verified: true, CodeCorrect: false, ErrorMessage: customStatus.ErrorMessage};
  } else if (customStatus?.CodeCorrect) {
    return customStatus;
  }

  return null;
}

export const postVerificationCodeForChallengeType = async(
  instanceId: string,
  code: string,
  challengeType: VerificationType,
): Promise<any> => {   
  const verificationResponse = await getInstanceVerification(instanceId);

  if (!!verificationResponse && verificationResponse.ErrorMessage !== "Incorrect code.") {
    return verificationResponse;
  }

  let challengeName;

  switch (challengeType) {
    case VerificationType.Email:
      challengeName = 'EmailChallengeResponse';
      break;
    case VerificationType.PhoneNumber:
      challengeName = 'SmsChallengeResponse';
      break;
  }

  await api.post<any>(`/Verification/${instanceId}/raiseEvent/${challengeName}`, { Code: code });

  let timeout = 0;

  while(timeout < 8) {
    await sleep(1500);
    const firstTry = await getInstanceVerification(instanceId);
    if (!!firstTry) {
      return firstTry;
    }

    await sleep(2500);
    const secondTry = await getInstanceVerification(instanceId);
    if (!!secondTry) {
      return secondTry;
    }

    timeout += 1;
  }

  return { CodeCorrect: false, ErrorMessage: "Failed to verify the code." };
}

export default {
  startEmailVerification,
  startPhoneNumberVerification,
  postVerificationCodeForChallengeType,
};
